/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { ComparisonOperator } from 'cio/utils/operators';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import AttributeCondition from 'cio/app-models/conditions/attribute';
import { ModelType } from 'cio/utils/conversions';
import { action, computed, set } from '@ember/object';
import { JsonAttributeOperator, MAX_JSON_DEPTH } from 'cio/utils/conditions/json-attributes';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import AnalyticsService from 'cio/services/analytics';
import { JsonConditionEditedInterface, JsonConditionEditType } from 'cio/utils/analytics/transforms/segment';
import { task, timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import SegmentModel from 'cio/models/segment';
import { isEmpty } from '@ember/utils';
import CampaignModel from 'cio/models/campaign';
import { OperatorGroup } from 'cio/components/condition-editor/attribute';
import { ObjectAttributeSource } from 'cio/app-models/conditions/object-attribute';

interface BaseCioConditionEditorAttributeArgs {
  attributeNames?: string[];
  showDateInequalityOperators?: boolean;
  actionItems?: { label: string; value: string }[];
  customerItems?: { label: string; value: string }[];
  changeType?: () => unknown;
  disabled?: boolean;
  allowComparisonOperator?: boolean;
  onConditionChange?: () => unknown;
  isJsonChild?: boolean;
  trackJsonValueEdit?: () => unknown;
  segment?: SegmentModel;
  onRemove: () => unknown;
  onDuplicate: () => unknown;
  canDuplicate?: boolean;
  modelType?: ModelType;
  campaign?: CampaignModel;
  showArrayConditionTypeSelector?: boolean;
  parentHasArrayValueInfoTooltip?: boolean;
  allowedOperators?: string[];
  isObject?: boolean;
  placeholder?: string;
  allowedOperatorGroups?: OperatorGroup[];
  attributeSourceOptions?: { label: ObjectAttributeSource; value: string }[];
  prefixText?: string;
}

interface AttributeConditionArgs extends BaseCioConditionEditorAttributeArgs {
  condition: AttributeCondition;
}

interface EventAttributeConditionArgs extends BaseCioConditionEditorAttributeArgs {
  condition: EventAttributeCondition;
}

type CioConditionEditorAttributeArgs = AttributeConditionArgs | EventAttributeConditionArgs;

function isBasicSupportedJsonOperator(operator: string): boolean {
  return (
    operator === JsonAttributeOperator.ArrayOrObjectHasPropertyOrValue ||
    operator === JsonAttributeOperator.ObjectHasProperty
  );
}

export default class CioConditionEditorAttribute extends Component<CioConditionEditorAttributeArgs> {
  @service analytics!: AnalyticsService;

  @computed('args.condition.editableCondition')
  get editableCondition(): AttributeCondition | EventAttributeCondition {
    let { condition } = this.args;
    return condition.editableCondition;
  }

  get isJsonChild() {
    return this.args.condition.isJsonChild;
  }

  get showDateInequalityOperators() {
    return this.args.showDateInequalityOperators ?? true;
  }

  get canRemove() {
    return !this.isJsonChild;
  }

  @computed('editableCondition.operator')
  get canDuplicate() {
    // disabling duplicating of json attribute operators due to the fact that they need more
    // special handling. when a user changes the operator, the component controls additional fields presented.
    // duplicating the condition does not present the interface the user is expecting.
    // TODO: Refactor and handle json attribute operators
    if (isBasicSupportedJsonOperator(this.editableCondition.operator)) {
      return false;
    }
    return this.args.canDuplicate === true && !this.isJsonChild;
  }

  @computed('args.parentHasArrayValueInfoTooltip', 'editableCondition.arrayConditionType', 'isJsonChild')
  get showArrayValueInfoTooltip() {
    return (
      this.isJsonChild &&
      this.editableCondition?.arrayConditionType === 'value' &&
      !(this.args.parentHasArrayValueInfoTooltip ?? false)
    );
  }

  @computed('args.condition.isAdvancedJsonMode', 'editableCondition.name')
  get canSwitchToBasicMode() {
    if (!this.args.condition.isAdvancedJsonMode) {
      return true;
    }

    if (isEmpty(this.editableCondition.name)) {
      return true;
    }

    let conditionItems = this.editableCondition.nameStringToJsonItems();
    return (
      conditionItems.length > 0 &&
      conditionItems.length <= MAX_JSON_DEPTH + 1 &&
      conditionItems.every((c) => c.operator !== JsonAttributeOperator.ArrayIndexHasPropertyOrValue)
    );
  }

  @computed(
    'isJsonChild',
    'args.condition.{hasJSONCondition,isAdvancedJsonMode}',
    'editableCondition.{childCondition,hasJSONOperator}',
  )
  get shouldShowBasicAdvancedToggle() {
    let { editableCondition } = this;
    let { condition } = this.args;

    if (this.isJsonChild) {
      return false;
    }

    if (condition.isAdvancedJsonMode || editableCondition.hasJSONOperator) {
      return true;
    }

    if (!condition.hasJSONCondition) {
      return false;
    }

    return editableCondition.childCondition != null;
  }

  @tracked isShowingArrayConditionConversionConfirmationModal = false;

  @action
  showArrayConditionConversionConfirmationModal() {
    this.isShowingArrayConditionConversionConfirmationModal = true;
  }

  @action
  hideArrayConditionConversionConfirmationModal() {
    this.isShowingArrayConditionConversionConfirmationModal = false;
  }

  @action
  maybeToggleAdvancedMode() {
    if (this.args.condition.isAdvancedJsonMode) {
      let currentConditionItems = this.editableCondition.nameStringToJsonItems();
      let hasUnsupportedArrayOperators = currentConditionItems.some(
        (c) => c.operator === JsonAttributeOperator.ArrayHasPropertyOrValue,
      );
      if (hasUnsupportedArrayOperators) {
        // If the condition has "A[].B"-like expressions, we will convert them
        // to A?.B instead - but we warn the user with a modal first and ask to
        // confirm
        this.showArrayConditionConversionConfirmationModal();
        return;
      }

      this.toggleAdvancedMode();
    } else {
      this.toggleAdvancedMode();
    }
  }

  @action
  convertConditionsAndToggleAdvancedMode() {
    set(this.args.condition, 'name', this.args.condition.name.replace(/\[\]/g, '?'));
    this.hideArrayConditionConversionConfirmationModal();
    this.toggleAdvancedMode();
  }

  @action
  toggleAdvancedMode() {
    this.args.condition.toggleAdvancedJsonMode();
  }

  previousOperator: string | null = null;

  @action
  onOperatorChanged() {
    if (
      isBasicSupportedJsonOperator(this.editableCondition.operator) &&
      (this.previousOperator == null || !isBasicSupportedJsonOperator(this.previousOperator))
    ) {
      this.trackJsonConditionAddedOrDeleted(JsonConditionEditType.Add);
    }

    if (
      !isBasicSupportedJsonOperator(this.editableCondition.operator) &&
      this.previousOperator != null &&
      isBasicSupportedJsonOperator(this.previousOperator)
    ) {
      this.trackJsonConditionAddedOrDeleted(JsonConditionEditType.Delete);
    }

    this.previousOperator = this.editableCondition.operator;
  }

  @action
  onNotOperatorChanged() {
    if (this.isJsonChild) {
      this.args.trackJsonValueEdit?.();
      return;
    }

    if (this.args.condition.isAdvancedJsonMode || this.editableCondition.childCondition != null) {
      this.trackJsonValueEdit();
    }
  }

  @action
  trackJsonValueEdit() {
    this.trackJsonConditionEdited.perform(JsonConditionEditType.Edit, true);
  }

  trackJsonConditionEdited = task({ restartable: true }, async (editType: JsonConditionEditType, debounce = false) => {
    if (debounce === true) {
      await timeout(config.APP.segmentValueEditDebounceDelay);
    }

    this.analytics.track('segment', 'json_condition_edited', {
      model: this.args.segment,
      interface: this.args.condition.isAdvancedJsonMode
        ? JsonConditionEditedInterface.Advanced
        : JsonConditionEditedInterface.Basic,
      editType,
    });
  });

  @action
  trackJsonConditionAddedOrDeleted(editType: JsonConditionEditType.Add | JsonConditionEditType.Delete) {
    this.analytics.track('segment', 'json_condition_edited', {
      model: this.args.segment,
      interface: this.args.condition.isAdvancedJsonMode
        ? JsonConditionEditedInterface.Advanced
        : JsonConditionEditedInterface.Basic,
      editType,
    });
  }

  @action
  onRemove() {
    if (this.args.condition.hasJSONCondition || this.editableCondition.hasJSONOperator) {
      this.trackJsonConditionAddedOrDeleted(JsonConditionEditType.Delete);
    }
    this.args.onRemove();
  }

  @computed('args.{condition.comparisonOperator,isJsonChild}')
  get autoSuggestValues() {
    if (this.args.condition instanceof EventAttributeCondition) {
      return this.args.condition.comparisonOperator !== ComparisonOperator.Event;
    }
    return !this.isJsonChild;
  }

  get placeholder() {
    if (!isEmpty(this.args.placeholder)) {
      return this.args.placeholder;
    }
    if (this.isJsonChild) {
      return 'property';
    }
    // if no specific case is met then we drill down as undefined, to "unset"
    return undefined;
  }
}
